import React, { Component } from 'react';
import pEntitiesVideo from './assets/video/web/pentities-demo.mp4';
import './mainpage.css';
import whoWeAreImage from './assets/2.webp';
import {
  MailFilled,
  SearchFilled,
  SearchInfoFilled,
  OrganizationFilled,
  PeopleFilled,
  ScalesFilled,
  MoneyFilled,
  TicketDiagonalFilled,
} from '@fluentui/react-icons';
import NavBar from './NavBar';

interface MainPageProps {}

interface MainPageState {
  isMenuOpen: boolean;
  currentTestimonial: number;
  isProductsMenuOpen: boolean;
  isSolutionsMenuOpen: boolean;
  isVideoOpen: boolean;
  shareCopied: boolean;
}

class MainPage extends Component<MainPageProps, MainPageState> {
  private testimonialInterval: NodeJS.Timeout | null = null;
  
  constructor(props: MainPageProps) {
    super(props);
    this.state = {
      isMenuOpen: false,
      currentTestimonial: 0,
      isProductsMenuOpen: false,
      isSolutionsMenuOpen: false,
      isVideoOpen: false,
      shareCopied: false
    };
  }

  componentDidMount() {
    // Auto-rotate testimonials every 5 seconds
    this.testimonialInterval = setInterval(() => {
      this.nextTestimonial();
    }, 5000);
  }

  componentWillUnmount() {
    if (this.testimonialInterval) {
      clearInterval(this.testimonialInterval);
    }
  }

  nextTestimonial = () => {
    this.setState((prevState) => ({
      currentTestimonial: (prevState.currentTestimonial + 1) % 3
    }));
  };

  prevTestimonial = () => {
    this.setState((prevState) => ({
      currentTestimonial: prevState.currentTestimonial === 0 ? 2 : prevState.currentTestimonial - 1
    }));
  };

  setTestimonial = (index: number) => {
    this.setState({ currentTestimonial: index });
  };

  toggleMenu = () => {
    this.setState({ isMenuOpen: !this.state.isMenuOpen });
  };

  toggleProductsMenu = () => {
    this.setState({ isProductsMenuOpen: !this.state.isProductsMenuOpen });
  };

  closeProductsMenu = () => {
    this.setState({ isProductsMenuOpen: false });
  };

  toggleSolutionsMenu = () => {
    this.setState({ isSolutionsMenuOpen: !this.state.isSolutionsMenuOpen });
  };

  closeSolutionsMenu = () => {
    this.setState({ isSolutionsMenuOpen: false });
  };

  openVideo = () => {
    this.setState({ isVideoOpen: true });
  };

  closeVideo = () => {
    this.setState({ isVideoOpen: false });
  };

  shareVideo = async () => {
    const shareUrl = new URL(pEntitiesVideo, window.location.origin).href;
    const shareData = {
      title: 'pEntities See the platform in action',
      text: 'Check out how pEntities transforms the way businesses manage entities inside Microsoft 365.',
      url: shareUrl,
    };
    if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
      try {
        await navigator.share(shareData);
      } catch (_) {}
    } else {
      try {
        await navigator.clipboard.writeText(shareUrl);
        this.setState({ shareCopied: true });
        setTimeout(() => this.setState({ shareCopied: false }), 2500);
      } catch (_) {}
    }
  };

  scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    this.setState({ isMenuOpen: false, isProductsMenuOpen: false, isSolutionsMenuOpen: false });
  };

  render() {
    return (
      <div className="main-page">
        {/* Top Navigation */}
        <NavBar showHome={false} />

        {/* Hero Section */}

        <section id="hero" className="hero-section">
          <div className="hero-video-background">
            <video 
              preload="metadata" 
              autoPlay 
              loop 
              muted 
              playsInline
              className="hero-background-video"
            >
              <source src={require('./assets/video/web/14360493_1920_1080_24fps.mp4')} type="video/mp4" />
            </video>
          </div>
          <div className="hero-overlay"></div>
          <div className="container">
            <div className="hero-container">
              <div className="hero-content">
                <h1 className="hero-title">Change the way of thinking</h1>
                <p className="hero-subtitle">
                At Meta-365, we are dedicated to transforming the way businesses operate by leveraging the power of Microsoft 365 and Azure. Our mission is to create cutting-edge applications that enhance productivity, streamline operations, and drive innovation.
                </p>
                <div className="hero-cta">
                  <a
                    href="https://outlook.office.com/book/META365Bookingforademo@e-study.gr/?ismsaljsauthenabled"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hero-cta-btn"
                  >
                    Schedule a Demo
                  </a>
                </div>
              </div>
              {/* <div className="hero-image">
                <img loading="lazy" decoding="async" src={heroImage} alt="Meta-365 Platform" />
              </div> */}
            </div>
          </div>
        </section>

        {/* Who We Are Section */}
        <section id="whoweare" className="whoweare-section">
          <div className="container">
            <div className="whoweare-layout">
              <div className="whoweare-content">
                <span className="whoweare-label">Who We Are</span>
                <h2 className="whoweare-title">Our Mission</h2>
                <p>
                  Meta-365 is a forward-thinking technology company specializing in the development of applications for Microsoft 365 and Azure. Founded on the principles of innovation, quality, and customer satisfaction, we strive to be at the forefront of the digital transformation landscape.
                </p>
              </div>
              <div className="whoweare-image">
                <img loading="lazy" decoding="async" src={whoWeAreImage} alt="Who We Are" />
              </div>
            </div>
          </div>
        </section>

        {/* Solutions Dark Zone */}
        <section id="solutions-dark" className="solutions-dark-section">
          <div className="container">
            <h2 className="solutions-dark-title">Our Solutions</h2>
            <p className="solutions-dark-subtitle">
              Enterprise power, instantly available our leading products, available through <b>Microsoft Marketplace</b>.
            </p>
            {/* pEntities-Core platform tree of dependent modules */}
            <div className="solution-tree">
              <div className="tree-root">
                <a href="/solutions/pentities" className="solution-dark-card solution-dark-card-small tree-card solution-core-card">
                  <span className="solution-core-badge">The platform core</span>
                  <div className="solution-dark-icon">
                    <OrganizationFilled className="product-icon orange" />
                  </div>
                  <h3>pEntities-Core</h3>
                  <p>The core that every module below is built on and runs on.</p>
                </a>
              </div>

              <div className="tree-branch">
                <div className="tree-node">
                  <a href="/solutions/ticketing" className="solution-dark-card solution-dark-card-small tree-card">
                    <div className="solution-dark-icon">
                      <TicketDiagonalFilled className="product-icon purple" />
                    </div>
                    <h3>Ticketing Module</h3>
                    <p>IT, HR, finance or legal requests real-time tracking, approvals, priorities and SLAs.</p>
                  </a>
                </div>
                <div className="tree-node">
                  <a href="/solutions/hr-template" className="solution-dark-card solution-dark-card-small tree-card">
                    <div className="solution-dark-icon">
                      <PeopleFilled className="product-icon teal" />
                    </div>
                    <h3>HR Module</h3>
                    <p>Employee records, onboarding, leave tracking and compliance all within Microsoft 365.</p>
                  </a>
                </div>
                <div className="tree-node">
                  <a href="/solutions/finance" className="solution-dark-card solution-dark-card-small tree-card">
                    <div className="solution-dark-icon">
                      <MoneyFilled className="product-icon green" />
                    </div>
                    <h3>Finance Module</h3>
                    <p>Invoices, expenses, budgets and receivables by fiscal year with full traceability.</p>
                  </a>
                </div>
                <div className="tree-node">
                  <a href="/solutions/case-management" className="solution-dark-card solution-dark-card-small tree-card">
                    <div className="solution-dark-icon">
                      <ScalesFilled className="product-icon burgundy" />
                    </div>
                    <h3>Legal Module</h3>
                    <p>Track matters, contracts, deadlines and client communications with AI document intelligence.</p>
                  </a>
                </div>
              </div>
            </div>

            {/* Independent add-ins & tools */}
            <div className="solutions-group-label">
              <span>Standalone add-ins used independently</span>
            </div>
            <div className="solutions-dark-grid solutions-dark-grid-small">
              <a href="/solutions/pshareemail" className="solution-dark-card solution-dark-card-small">
                <div className="solution-dark-icon">
                  <MailFilled className="product-icon blue" />
                </div>
                <h3>PShareEmail</h3>
                <p>Efficiently save your emails to SharePoint, OneDrive, or Teams. Navigate documents, copy links, and easily locate emails or documents with our comprehensive Outlook add-in.</p>
              </a>
              <a href="/solutions/docsearch" className="solution-dark-card solution-dark-card-small">
                <div className="solution-dark-icon">
                  <SearchFilled className="product-icon purple" />
                </div>
                <h3>DocSearch Tool</h3>
                <p>Advanced search capabilities for Microsoft 365. Find documents, emails, and content across your entire organization quickly and efficiently.</p>
              </a>
              <a href="/solutions/searchassistant" className="solution-dark-card solution-dark-card-small">
                <div className="solution-dark-icon">
                  <SearchInfoFilled className="product-icon green" />
                </div>
                <h3>Search Assistant</h3>
                <p>Boost productivity with intelligent search for Microsoft 365 Teams and Outlook. Locate information faster and work smarter.</p>
              </a>
            </div>
          </div>
        </section>



        {/* Proof in Practice Section */}
        <section id="proof-in-practice" className="products-highlight">
          <div className="container">
            <div className="proof-layout">
              <div className="proof-content">
                <span className="proof-label">pEntities in Action</span>
                <h2 className="proof-title">
                  How Bioland Cyprus transformed request management with pEntities-Ticketing
                </h2>
                <p>
                  At pEntities, our goal has always been simple: help organizations eliminate operational friction, increase visibility, and automate repetitive work using the tools they already use every day.
                </p>
                <p>
                  One example of this impact comes from one of our customers, who adopted pEntities Ticketing to replace the traditional email-based request management process.
                </p>
                <a href="/bioland" className="proof-link">Read the story</a>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section id="testimonials" className="testimonials-section">
          <div className="container">
            <div className="proof-layout" >
              <div className="proof-content" >
            <span className="proof-label" style={{marginLeft: '0px'}}>TESTIMONIALS</span>
            <div className="testimonials-carousel">
              
              <div className="testimonial-track">
                {(() => {
                  const testimonials = [
                                        {
                      quote: "PShareEmail and Search Assistant apps for Microsoft 365 Teams and Outlook have significantly boosted the productivity of our employees.",
                      name: "Stelios Georgiou",
                      title: "Information Technology Manager, Bioland Energy (Cyprus)"
                    },
                    {
                      quote: "Monitoring the progress of offers, agreements and customer follow up got as easy as it gets. With Meta-365 apps my team and the whole organization have got in-line.",
                      name: "Thanopoulos Dimitrios",
                      title: "Head & Co-Founder @4Fluids"
                    },
                    {
                      quote: "Meta-365 apps save hours of time for me and my team spending on document management issues! In addition, they clear up to all of us the good practice in using meta data appropriately.",
                      name: "George Mazarakis",
                      title: "CEO and Co-Founder, PCC Hellas"
                    },

                  ];
                  const testimonial = testimonials[this.state.currentTestimonial];
                  return (
                    <div className="testimonial-card active">
                      <p className="testimonial-quote">&ldquo;{testimonial.quote}&rdquo;</p>
                      <div className="testimonial-author">
                        <span className="testimonial-name">{testimonial.name}</span>
                        <span className="testimonial-title">{testimonial.title}</span>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
            <div className="testimonial-dots">
              {[0, 1, 2].map((index) => (
                <button
                  key={index}
                  className={`testimonial-dot ${index === this.state.currentTestimonial ? 'active' : ''}`}
                  onClick={() => this.setTestimonial(index)}
                  aria-label={`Go to testimonial ${index + 1}`}
                />
              ))}
            </div>
              </div>
            </div>
          </div>
        </section>

        {/* Automation Promo Section */}
        <section id="automation-promo" className="automation-promo-section">
          <div className="automation-promo-inner">
            <div className="automation-promo-content">
              {/* <span className="automation-promo-label">New</span> */}
              <h2>What if nothing ever slipped through the cracks again?</h2>
              <p>
                Approvals that route themselves. Deadlines that enforce themselves. Follow-ups that send themselves. Our automation platform runs inside Microsoft Teams no code, no third-party tools, no IT setup. Define the trigger, set the conditions, watch it execute. Every time. Precisely. Regardless of who's in the office.
              </p>
            </div>
            <div className="automation-promo-media">
              <div className="video-preview-block">
                <p className="video-preview-label">See the platform in action</p>
                <div className="video-preview-wrapper" onClick={this.openVideo} role="button" aria-label="Play pEntities video">
                  <video
                    className="video-preview-thumbnail"
                    src={`${pEntitiesVideo}#t=0.001`}
                    muted
                    playsInline
                    preload="metadata"
                  />
                  <div className="video-play-overlay">
                    <svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" className="video-play-icon">
                      <circle cx="32" cy="32" r="32" fill="rgba(0,0,0,0.55)" />
                      <polygon points="24,16 52,32 24,48" fill="#ffffff" />
                    </svg>
                  </div>
                </div>
                <button
                  className="video-share-btn"
                  onClick={this.shareVideo}
                  aria-label="Share this video"
                >
                  <svg viewBox="0 0 20 20" fill="currentColor" className="video-share-icon" xmlns="http://www.w3.org/2000/svg">
                    <path d="M15 14a2 2 0 00-1.32.5L7.4 10.7a2.1 2.1 0 000-1.4l6.28-3.8A2 2 0 1012.5 4l-6.28 3.8a2 2 0 100 4.4L12.5 16A2 2 0 1015 14z" />
                  </svg>
                  {this.state.shareCopied ? 'Link copied!' : 'Share this video'}
                </button>
                <a href="/solutions/automation" className="automation-promo-btn">See how it works</a>
              </div>
            </div>
          </div>
        </section>

        {/* Enterprise Solutions Section */}
        {/* <section id="enterprise-solutions" className="enterprise-solutions-section">
          <div className="container">
            <h2 className="section-title-center">Enterprise power, instantly available.</h2>
            <p className="section-subtitle-center">
              Meta‑365 runs your most critical journeys with precision and speed. Check out our leading solutions
            </p>
            <div className="enterprise-cards-grid">
              <a href="/solutions/automation" className="enterprise-card">
                <div className="enterprise-card-icon-wrapper">
                  <OrganizationFilled className="enterprise-card-icon automation" />
                </div>
                <h3>Automate with AI Agents</h3>
                <div className="enterprise-card-arrow">→</div>
              </a>
              <a href="/solutions/hr-template" className="enterprise-card">
                <div className="enterprise-card-icon-wrapper">
                  <PeopleFilled className="enterprise-card-icon hr" />
                </div>
                <h3>Transform HR Operations</h3>
                <div className="enterprise-card-arrow">→</div>
              </a>
              <a href="/solutions/case-management" className="enterprise-card">
                <div className="enterprise-card-icon-wrapper">
                  <ScalesFilled className="enterprise-card-icon legal" />
                </div>
                <h3>Legal Case Management</h3>
                <div className="enterprise-card-arrow">→</div>
              </a>
            </div>
          </div>
        </section> */}

        {/* Products Section (moved into Solutions Dark Zone above) */}
        {false && (
        <section id="products" className="solutions-section">
          <div className="container">
            <h2 className="section-title" >Our Products. Enterprise power, instantly available.</h2>
            <p className="section-subtitle">
              Meta‑365 runs your most critical journeys with precision and speed. Check out our leading solutions. Available through <b>Microsoft Marketplace</b>
            </p>
            <div className="solutions-grid">
              <div className="solution-card" >
                <div className="card-image">
                  <MailFilled className="product-icon blue" />
                </div>
                <h3>PShareEmail</h3>
                <p>Efficiently save your emails to SharePoint, OneDrive, or Teams. Navigate documents, copy links, and easily locate emails or documents with our comprehensive Outlook add-in.</p>
                <a href="/solutions/pshareemail" className="card-link">Learn more →</a>
              </div>
              <div className="solution-card">
                <div className="card-image">
                  <SearchFilled className="product-icon purple" />
                </div>
                <h3>DocSearch Tool</h3>
                <p>Advanced search capabilities for Microsoft 365. Find documents, emails, and content across your entire organization quickly and efficiently.</p>
                <a href="/solutions/docsearch" className="card-link">Learn more →</a>
              </div>
              <div className="solution-card">
                <div className="card-image">
                  <SearchInfoFilled className="product-icon green" />
                </div>
                <h3>Search Assistant</h3>
                <p>Boost productivity with intelligent search for Microsoft 365 Teams and Outlook. Locate information faster and work smarter.</p>
                <a href="/solutions/searchassistant" className="card-link">Learn more →</a>
              </div>
              <div className="solution-card">
                <div className="card-image">
                  <OrganizationFilled className="product-icon orange" />
                </div>
                <h3>pEntities-Core</h3>
                <p>Streamline entity management and metadata handling for Microsoft 365. Organize and track important business entities with intelligent tagging and classification.</p>
                <a href="/solutions/pentities" className="card-link">Learn more →</a>
              </div>
              <div className="solution-card">
                <div className="card-image">
                  <PeopleFilled className="product-icon teal" />
                </div>
                <h3>pEntities-HR Module</h3>
                <p>Transform HR operations with intelligent digital workspace. Manage employee records, onboarding, leave tracking, and compliance all within Microsoft 365.</p>
                <a href="/solutions/hr-template" className="card-link">Learn more →</a>
              </div>
              <div className="solution-card">
                <div className="card-image">
                  <ScalesFilled className="product-icon burgundy" />
                </div>
                <h3>pEntities-Legal Module</h3>
                <p>Complete legal case management solution. Track matters, contracts, deadlines, and client communications with AI-powered document intelligence.</p>
                <a href="/solutions/case-management" className="card-link">Learn more →</a>
              </div>
              <div className="solution-card">
                <div className="card-image">
                  <PeopleFilled className="product-icon teams" />
                </div>
                <h3>pEntities-Employee Hub Module</h3>
                <p>Your complete HR world right inside Microsoft Teams. Leave management, task tracking, team presence, in-app messaging, and meeting scheduling all in one self-service tab.</p>
                <a href="/solutions/hr-hub" className="card-link">Learn more →</a>
              </div>
            </div>
          </div>
        </section>
        )}

        {/* About/CTA Section */}
        <section id="about" className="about-cta-section">
          <div className="container">
            <h2 className="section-title">Ready to Transform Your Business?</h2>
            <p className="section-subtitle">
              With a team of experienced developers, designers, and strategists and proven experience in Microsoft 365 transformation across large organizations we are committed to delivering solutions that meet the unique needs of our clients.
              From strategy to deployment, we help enterprises modernize their workplace, automate operations, and unlock the full potential of Microsoft 365 and Azure.
            </p>
            <div className="cta-actions">
              <button className="cta-proof-btn" onClick={() => window.open('https://outlook.office.com/book/META365Bookingforademo@e-study.gr/?ismsaljsauthenabled', '_blank')}>Schedule a Demo</button>
              <button className="cta-proof-btn" onClick={() => window.location.href = '/get-started'}>Get Started Now</button>
              <button className="cta-proof-btn" onClick={() => window.location.href = '/contactus'}>Contact Us</button>
            </div>
          </div>
        </section>

        {/* Video Modal */}
        {this.state.isVideoOpen && (
          <div className="video-modal-overlay" onClick={this.closeVideo} role="dialog" aria-modal="true" aria-label="pEntities video">
            <div className="video-modal-content" onClick={(e) => e.stopPropagation()}>
              <button className="video-modal-close" onClick={this.closeVideo} aria-label="Close video">✕</button>
              <video
                className="video-modal-player"
                src={pEntitiesVideo}
                controls
                autoPlay
                playsInline
              />
            </div>
          </div>
        )}

        {/* Footer */}
        <footer className="footer">
          <div className="container">
            <div className="footer-grid">
              <div className="footer-column">
                <h4>Products</h4>
                <ul>
                  <li><a href="/solutions/pentities">pEntities</a>
                    <ul className="footer-subnav">
                      <li><a href="/solutions/automation">Automation Module</a></li>
                      <li><a href="/solutions/ticketing">Ticketing Module</a></li>
                      <li><a href="/solutions/hr-template">HR Module</a></li>
                      <li><a href="/solutions/case-management">Legal Module</a></li>
                      <li><a href="/solutions/hr-hub">Employee Module</a></li>
                    </ul>
                  </li>
                  <li><a href="/solutions/pshareemail">PShareEmail</a></li>
                  <li><a href="/solutions/docsearch">DocSearch Tool</a></li>
                  <li><a href="/solutions/searchassistant">Search Assistant</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Company</h4>
                <ul>
                  <li><a href="/about">About Us</a></li>
                  <li><a href="/get-started">Get Started</a></li>
                  <li><a href="/contactus">Contact Us</a></li>
                  <li><a href="/plans-pricing">Plans & Pricing</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Support</h4>
                <ul>
                  <li><a href="/knowledge">Knowledge Base</a></li>
                  <li><a href="/technical-support">Technical Support</a></li>
                </ul>
              </div>
              <div className="footer-column">
                <h4>Contact Us</h4>
                <div className="footer-office">
                  <p className="office-name">Athens Office</p>
                  <p>365 Kifisias Avenue, Kifisia, 14561</p>
                  <p>Ground Floor, Inside the Arcade</p>
                  <p>Tel: (+30)-210-260-3764</p>
                </div>
                <div className="footer-office">
                  <p className="office-name">Thessaloniki Office</p>
                  <p>15 Nikiforou Ouranou & Minotavrou Streets</p>
                  <p>Thessaloniki, 54627, PORTO CENTER, Building G1, 3rd Floor</p>
                  <p>Tel: (+30)-231-005-3361</p>
                </div>
                <div className="footer-office">
                  <p className="office-name">Crete Office</p>
                  <p>Kisamos, Chania</p>
                  <p>Tel: (+30)-2822-051309</p>
                </div>
                <p className="footer-email">info@meta-365.com</p>
              </div>
            </div>
            <div className="footer-bottom">
              <p>&copy; 2026 Meta-365. All rights reserved.</p>
              <div className="footer-social">
                <a href="https://www.linkedin.com/company/meta-365" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"><img loading="lazy" decoding="async" src="data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20viewBox='0%200%2024%2024'%20fill='white'%3E%3Cpath%20d='M4.98%203.5C4.98%204.88%203.87%206%202.5%206S0%204.88%200%203.5%201.12%201%202.5%201s2.48%201.12%202.48%202.5zM.25%208h4.5v13H.25V8zM8.5%208h4.31v1.78h.06c.6-1.14%202.07-2.34%204.26-2.34C21.5%207.44%2022.5%2010%2022.5%2014.02V21H18v-6.1c0-1.46-.03-3.33-2.03-3.33-2.03%200-2.34%201.58-2.34%203.22V21H8.5V8z'/%3E%3C/svg%3E" alt="LinkedIn" width="18" height="18" /></a>
                <a href="https://www.facebook.com/profile.php?id=61589941284929" target="_blank" rel="noopener noreferrer" aria-label="Facebook"><img loading="lazy" decoding="async" src="data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20viewBox='0%200%2024%2024'%20fill='white'%3E%3Cpath%20d='M24%2012.07C24%205.4%2018.63%200%2012%200S0%205.4%200%2012.07C0%2018.1%204.39%2023.1%2010.13%2024v-8.44H7.08v-3.49h3.05V9.41c0-3.02%201.79-4.69%204.53-4.69%201.31%200%202.68.24%202.68.24v2.97h-1.51c-1.49%200-1.96.93-1.96%201.89v2.25h3.33l-.53%203.49h-2.8V24C19.61%2023.1%2024%2018.1%2024%2012.07z'/%3E%3C/svg%3E" alt="Facebook" width="18" height="18" /></a>
              </div>
              <div className="footer-links">
                <a href="/privacy-policy">Privacy Policy</a>
                <a href="/terms-of-use">Terms & Conditions</a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    );
  }
}

export default MainPage;

